import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { X } from 'lucide-react';
import { PaymentResult } from './ui/PaymentResult';
import { TapToPayIcon } from './ui/icons/TapToPayIcon';
import { TapToPayReceipt } from './TapToPayReceipt';
import { type PrefetchedTerminalIntent, type TapToPayState, type UseTapToPayReturn } from '@/hooks/useTapToPay';
import { copyToClipboard } from '@/lib/capacitor';
import { useModalLayer } from '@/lib/layerManager';
import type { PaymentTarget } from '@/types';
import {
    getTapToPayDiagnosticsReport,
    markTapToPayIntent,
    recordTapToPayDiagnostic,
    requestTapToPayLocationPermission,
    tapToPayFailureHeadline,
    tapToPayName,
} from '@/lib/tapToPay';

interface TapToPayModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Job or estimate being charged. */
    target: PaymentTarget;
    /** Amount in dollars (not cents) */
    totalAmount: number;
    /** Prefills the digital receipt Apple requires after every outcome (checklist §5.9). */
    clientEmail?: string;
    /** Shown on the receipt so the customer can tie it to their job. */
    invoiceNumber?: string;
    onSuccess: () => void;
    onDismiss: () => void;
    /** Stripe Terminal location ID (tml_xxx) for the company's connected account */
    terminalLocationId?: string;
    /**
     * Tap to Pay hook value owned by the parent (TakePaymentModal). The hook's
     * lifetime — and therefore the Stripe Terminal reader connection — is
     * bounded by the parent so reopening this modal is instant.
     */
    tapToPay: UseTapToPayReturn;
    /**
     * PaymentIntent created ahead of time by the parent while the user was
     * setting the amount. Held in a ref (not a prop value) so it can be cleared
     * the moment it is consumed — a client secret must never be replayed.
     */
    prefetchedIntentRef: MutableRefObject<PrefetchedTerminalIntent | null>;
}

function StatusLabel({ state }: { state: TapToPayState }) {
    switch (state) {
        case 'initializing': return <span>Initializing reader…</span>;
        case 'discovering':  return <span>Connecting to reader…</span>;
        case 'collecting':   return <span>Ready — tap your card now</span>;
        case 'confirming':   return <span>Confirming payment…</span>;
        default:             return null;
    }
}

const ACTIVE_STATES: TapToPayState[] = ['initializing', 'discovering', 'collecting', 'confirming'];

export function TapToPayModal({
    isOpen,
    onClose,
    target,
    totalAmount,
    clientEmail,
    invoiceNumber,
    onSuccess,
    onDismiss,
    terminalLocationId,
    tapToPay,
    prefetchedIntentRef,
}: TapToPayModalProps) {
    const { state, errorMessage, progress, startPayment, cancel, retry } = tapToPay;
    const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
    const successHandledRef = useRef(false);

    const layer = useModalLayer({
        enabled: isOpen && state !== 'succeeded',
        onClose,
    });

    /**
     * Take the prefetched intent, if any, and clear it. Consuming it exactly
     * once means a retry always creates a fresh PaymentIntent rather than
     * replaying a secret whose intent may have moved on.
     */
    const takePrefetchedIntent = (): PrefetchedTerminalIntent | null => {
        const intent = prefetchedIntentRef.current;
        prefetchedIntentRef.current = null;
        return intent;
    };

    // Start the payment flow as soon as the modal opens
    useEffect(() => {
        if (!isOpen) return;
        // 'failed' deliberately does not auto-start: the error screen stays up so
        // the user chooses Try Again.
        if (state === 'failed') return;

        void (async () => {
            // Defence in depth. `startPayment` only runs from idle/failed, so a
            // machine left mid-flow by an earlier attempt would make this modal open
            // onto a stale progress message and never start anything. Reset first so
            // a wedge can never survive reopening the screen.
            if (state !== 'idle') {
                recordTapToPayDiagnostic('state', 'Resetting a stuck Tap to Pay state on open', {
                    state,
                }, 'warn');
                await cancel();
            }

            // Resolve location before anything can reach collectPaymentMethod.
            // Stripe Terminal needs it, and the iOS SDK will otherwise raise
            // the dialog itself at confirm time — with the customer's card
            // already against the phone. Returns immediately once granted.
            await requestTapToPayLocationPermission();

            // Mark after the permission step so a one-time system dialog,
            // which is user-paced, isn't counted against the ready-time budget.
            markTapToPayIntent();

            const amountCents = Math.round(totalAmount * 100);
            await startPayment(target, amountCents, terminalLocationId, takePrefetchedIntent());
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Notify parent on success
    useEffect(() => {
        if (state === 'succeeded' && !successHandledRef.current) {
            successHandledRef.current = true;
            onSuccess();
        }
    }, [state, onSuccess]);

    useEffect(() => {
        if (state !== 'failed') {
            setCopyStatus('idle');
        }

        if (state !== 'succeeded') {
            successHandledRef.current = false;
        }
    }, [state, errorMessage]);

    if (!isOpen) return null;

    const isActive = ACTIVE_STATES.includes(state);
    const statusText = progress?.message ?? null;

    const handleCopyDiagnostics = async () => {
        try {
            const report = await getTapToPayDiagnosticsReport();
            const copied = await copyToClipboard(report);
            setCopyStatus(copied ? 'copied' : 'failed');
        } catch {
            setCopyStatus('failed');
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" style={layer.style}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm relative animate-in fade-in zoom-in-95 duration-200">

                {/* Header */}
                {state !== 'succeeded' && (
                    <div className="flex items-center justify-between p-4 border-b border-gray-100">
                        <h2 className="text-base font-semibold text-gray-900">{tapToPayName()}</h2>
                        <button
                            onClick={() => {
                                layer.onClose();
                                cancel().catch(() => {});
                            }}
                            className="p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-all"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                )}

                <div className="p-6">
                    {/* ── Active states: NFC animation ── */}
                    {isActive && (
                        <div className="flex flex-col items-center gap-6 py-4">
                            {/* Animated NFC icon */}
                            <div className="relative flex items-center justify-center w-28 h-28">
                                {/* Pulsing rings */}
                                <span className="absolute inset-0 rounded-full bg-indigo-100 animate-ping opacity-50" />
                                <span className="absolute inset-3 rounded-full bg-indigo-200 animate-ping opacity-40 animation-delay-150" />
                                <div className="relative z-10 flex items-center justify-center w-20 h-20 rounded-full bg-indigo-600 shadow-lg">
                                    <TapToPayIcon className="h-11 w-11 text-white" />
                                </div>
                            </div>

                            {/* Amount */}
                            <div className="text-center w-full">
                                <p className="text-3xl font-bold text-gray-900">
                                    ${totalAmount.toFixed(2)}
                                </p>
                                <p className="mt-2 text-sm text-gray-500">
                                    {statusText ?? <StatusLabel state={state} />}
                                </p>
                                {/* §3.6: determinate bar during first-run provisioning. */}
                                {progress?.progress != null && (
                                    <div className="mt-3 mx-auto w-40 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                                            style={{ width: `${Math.round(progress.progress * 100)}%` }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Cancel button (only during collecting) */}
                            <div className="flex flex-col items-center gap-2">
                                {state === 'collecting' && (
                                    <button
                                        onClick={() => {
                                            layer.onClose();
                                            cancel().catch(() => {});
                                        }}
                                        className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleCopyDiagnostics}
                                    className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                                >
                                    {copyStatus === 'copied' ? 'Diagnostics copied' : 'Copy diagnostics'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Failed state ── */}
                    {state === 'failed' && (
                        <PaymentResult
                            variant="error"
                            amount={totalAmount}
                            headline={tapToPayFailureHeadline(errorMessage)}
                            message={errorMessage ?? undefined}
                            primaryLabel="Try Again"
                            onPrimaryAction={async () => {
                                // "Try Again" is a fresh intent-to-pay gesture,
                                // so the ready-time budget restarts here too.
                                markTapToPayIntent();
                                await retry();
                                const amountCents = Math.round(totalAmount * 100);
                                await startPayment(target, amountCents, terminalLocationId, null);
                            }}
                            secondaryLabel="Cancel"
                            onSecondaryAction={() => {
                                // Must reset like the header X does, otherwise the
                                // state stays 'failed' and reopening shows the old
                                // error screen instead of starting a payment.
                                layer.onClose();
                                cancel().catch(() => {});
                            }}
                        />
                    )}

                    {state === 'failed' && (
                        <div className="mt-3 flex flex-col items-center gap-2 text-center">
                            {/* Apple §5.9: a receipt must be sendable after a decline too. */}
                            <div className="w-full text-left">
                                <TapToPayReceipt
                                    amount={totalAmount}
                                    outcome="declined"
                                    invoiceNumber={invoiceNumber}
                                    defaultEmail={clientEmail}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={handleCopyDiagnostics}
                                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                            >
                                {copyStatus === 'copied' ? 'Diagnostics copied' : 'Copy Tap to Pay diagnostics'}
                            </button>
                            {copyStatus === 'copied' && (
                                <p className="text-xs text-gray-500">Paste the copied diagnostics into a message or ticket.</p>
                            )}
                            {copyStatus === 'failed' && (
                                <p className="text-xs text-red-500">Could not copy diagnostics. Try again.</p>
                            )}
                        </div>
                    )}

                    {/* ── Succeeded state ── */}
                    {state === 'succeeded' && (
                        <>
                            <PaymentResult
                                variant="success"
                                amount={totalAmount}
                                primaryLabel="Back to Job"
                                onPrimaryAction={onDismiss}
                            />
                            <TapToPayReceipt
                                amount={totalAmount}
                                outcome="approved"
                                invoiceNumber={invoiceNumber}
                                defaultEmail={clientEmail}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
